// 長辺(cm)入力 → 該当する価格帯のバリエーションを選択し、仕上がり寸法を注文情報に入れる。
// Dawn はバリエーション切替のたびに variant-selects の中身を入れ替えるので、
// 入力値は商品IDごとに window に保持し、要素が作り直されたら復元する。
(() => {
  if (customElements.get('stckrs-length-input')) return;

  const state = (window.stckrsLength = window.stckrsLength || {});
  // 価格帯の切替中（フォームの variant id がまだ古い）の商品ID
  const pending = new Set();
  let resubmitForm = null;

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      pending.clear();
      // 切替待ちでカート追加を止めていたら、切替完了後にあらためて送信する
      if (resubmitForm) {
        const form = resubmitForm;
        resubmitForm = null;
        requestAnimationFrame(() => form.querySelector('[type="submit"]')?.click());
      }
    });
  }

  // 「長辺11〜15cm」→ { min: 11, max: 15 }、「長辺10cm」→ { min: 10, max: 10 }
  const parseBand = (value) => {
    const m = String(value).match(/(\d+)(?:\s*[〜~～-]\s*(\d+))?\s*cm/);
    return m ? { min: Number(m[1]), max: Number(m[2] || m[1]) } : null;
  };

  const formatCm = (n) => (Math.round(n * 10) / 10).toString().replace(/\.0$/, '');

  class StckrsLengthInput extends HTMLElement {
    connectedCallback() {
      this.fieldset = this.closest('fieldset');
      this.input = this.querySelector('[data-length-input]');
      this.result = this.querySelector('[data-length-result]');
      this.help = this.querySelector('[data-length-help]');
      this.lengthProp = this.querySelector('[data-length-prop]');
      this.sizeProp = this.querySelector('[data-size-prop]');
      this.min = Number(this.dataset.min) || 10;
      this.max = Number(this.dataset.max) || 100;
      try {
        this.ratios = JSON.parse(this.dataset.ratio || '{}') || {};
      } catch {
        this.ratios = {};
      }

      // number 入力の change が Dawn の variant-selects に伝わると選択処理が誤動作するので止める
      // 入力中も少し待って価格帯を切り替える（カートボタンを押す前に価格を確定させておくため）
      this.input.addEventListener('input', (event) => {
        event.stopPropagation();
        this.update({ selectBand: false });
        clearTimeout(this.selectTimer);
        this.selectTimer = setTimeout(() => this.update({ selectBand: true }), 400);
      });
      this.input.addEventListener('change', (event) => {
        event.stopPropagation();
        clearTimeout(this.selectTimer);
        this.update({ selectBand: true });
      });
      this.input.addEventListener('keydown', (event) => {
        // Enter でフォーム送信されないように、確定扱いにする
        if (event.key === 'Enter') {
          event.preventDefault();
          this.update({ selectBand: true });
        }
      });

      const saved = state[this.dataset.productId];
      if (saved) this.input.value = saved;
      this.update({ selectBand: false });
    }

    // 選択中のスタイル等に合う縦横比（W÷H）
    currentRatio() {
      const variantSelects = this.closest('variant-selects');
      if (variantSelects) {
        for (const checked of variantSelects.querySelectorAll('input[type="radio"]:checked')) {
          if (this.ratios[checked.value] != null) return Number(this.ratios[checked.value]);
        }
      }
      return Number(this.ratios.default) || null;
    }

    bandInputs() {
      return [...this.fieldset.querySelectorAll('.stckrs-length__bands input[type="radio"]')];
    }

    value() {
      const raw = this.input.value.trim();
      if (raw === '') return null;
      const n = Number(raw);
      if (!Number.isInteger(n) || n < this.min || n > this.max) return NaN;
      return n;
    }

    update({ selectBand }) {
      // Dawn の再描画で置き換えられた古い要素（タイマーの残り等）では何もしない
      if (!this.isConnected) return;
      const length = this.value();
      const valid = Number.isInteger(length);
      this.classList.toggle('is-invalid', Number.isNaN(length));
      this.input.setAttribute('aria-invalid', Number.isNaN(length) ? 'true' : 'false');

      if (!valid) {
        delete state[this.dataset.productId];
        this.lengthProp.disabled = true;
        this.sizeProp.disabled = true;
        this.result.textContent = Number.isNaN(length) ? `${this.min}〜${this.max}cmの1cm単位（整数）で入力してください` : '';
        return;
      }

      state[this.dataset.productId] = String(length);

      // 仕上がり寸法（長辺を基準に、もう一方を比率から計算）
      const ratio = this.currentRatio();
      let sizeText = '';
      if (ratio) {
        const w = ratio >= 1 ? length : length * ratio;
        const h = ratio >= 1 ? length / ratio : length;
        sizeText = `W${formatCm(w)}cm×H${formatCm(h)}cm`;
      }
      this.lengthProp.value = `${length}cm`;
      this.lengthProp.disabled = false;
      this.sizeProp.value = sizeText ? `約${sizeText}` : '';
      this.sizeProp.disabled = !sizeText;
      this.result.textContent = sizeText ? `仕上がり 約${sizeText}` : '';

      if (!selectBand) return;
      const target = this.bandInputs().find((radio) => {
        const band = parseBand(radio.value);
        return band && length >= band.min && length <= band.max;
      });
      if (target && !target.checked) {
        // Dawn のバリエーション切替を通常のクリックと同じ経路で動かす。
        // 切替は非同期（HTML取得後にフォームの variant id が変わる）なので、完了まで pending にする
        pending.add(this.dataset.productId);
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // カート追加前の確認。不正なら false
    validate() {
      const length = this.value();
      if (Number.isInteger(length)) {
        this.update({ selectBand: true });
        return true;
      }
      this.classList.add('is-invalid');
      this.result.textContent = `長辺を${this.min}〜${this.max}cmの1cm単位（整数）で入力してください`;
      this.input.focus();
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
  }

  customElements.define('stckrs-length-input', StckrsLengthInput);

  // カートに入れる前に長辺の入力を必須にする（Dawn の product-form より先に捕まえる）
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      // form.id は name="id"（バリエーションID）の入力欄に隠れるので属性から読む
      const formId = form.getAttribute('id');
      if (!formId) return;
      const lengthInput = [...document.querySelectorAll('[data-length-prop]')]
        .find((prop) => prop.getAttribute('form') === formId)
        ?.closest('stckrs-length-input');
      if (!lengthInput) return;
      if (!lengthInput.validate() || pending.has(lengthInput.dataset.productId)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (pending.has(lengthInput.dataset.productId)) resubmitForm = form;
      }
    },
    true
  );
})();
